/**
 * TanStack Query hooks for the single, canonical trip-review flow.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { reviewService } from '../services/review.service'
import { useAuth } from './use-auth'
import type { PendingTripReview, SubmitTripReviewInput } from '../types/review.types'

const reviewKeys = {
  all: ['reviews'] as const,
  eligibility: (rentalId: string) => [...reviewKeys.all, 'eligibility', rentalId] as const,
  pendingRoot: () => [...reviewKeys.all, 'pending-trip'] as const,
  pending: (userId: string) => [...reviewKeys.pendingRoot(), userId] as const,
}

/**
 * Return the oldest completed trip the current participant still needs to rate.
 *
 * The gate fails open on a transient read error: a network failure must never
 * trap someone outside the rest of the app.
 */
export function usePendingTripReview(options: { enabled?: boolean } = {}) {
  const { user } = useAuth()

  return useQuery({
    queryKey: reviewKeys.pending(user?.id ?? ''),
    queryFn: () => reviewService.getPendingTripReview(),
    // This drives an optional nudge, not the user's trip record. Do not spend
    // a root-level RPC on every app route only to show a sheet that belongs on
    // the Trips surface.
    enabled: !!user?.id && (options.enabled ?? true),
    retry: 1,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  })
}

export function useReviewEligibility(rentalId: string | undefined) {
  const { user } = useAuth()

  return useQuery({
    queryKey: reviewKeys.eligibility(rentalId ?? ''),
    queryFn: () => reviewService.checkEligibility(user!.id, rentalId!),
    enabled: !!user?.id && !!rentalId,
    retry: false,
    staleTime: 5 * 60 * 1000,
  })
}

/** Submit one rating for the complete trip and refresh every derived surface. */
export function useSubmitTripReview() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (input: SubmitTripReviewInput) => reviewService.submitTripReview(input),

    onSuccess: (review) => {
      // The dedicated screen navigates immediately after mutateAsync resolves.
      // Clear the exact submitted trip synchronously so the global gate cannot
      // flash the stale form while the authoritative refetch is in flight.
      queryClient.setQueriesData<PendingTripReview | null>(
        { queryKey: reviewKeys.pendingRoot() },
        (pending) => (pending?.rentalId === review.rentalId ? null : pending),
      )
      void queryClient.invalidateQueries({ queryKey: reviewKeys.all })

      if (review.reviewerRole === 'guest') {
        void queryClient.invalidateQueries({ queryKey: ['vehicles'] })
        void queryClient.invalidateQueries({
          queryKey: ['vehicle-detail', review.vehicleId],
        })
      }
    },
  })

  return {
    submitTripReview: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  }
}
